import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Link2, Search, UserPlus, Info } from "lucide-react";
import { customersApi, jobsApi, isValidSEPhone, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Admin-only. Two modes:
 *   1) Link job to EXISTING customer (search + explicit pick).
 *   2) Create NEW customer identity (form prefilled with job's name/phone).
 * Never auto-merges on name/phone. Shows possible matches before new creation.
 */
export const LinkCustomerDialog = ({ open, onClose, job, onLinked }) => {
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [searching, setSearching] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [phoneMissing, setPhoneMissing] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");
  const [possibleMatches, setPossibleMatches] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverErr, setServerErr] = useState("");

  useEffect(() => {
    if (open) {
      setMode("existing");
      setSearchTerm("");
      setResults([]);
      setPicked(null);
      setNewName(job?.customerName || "");
      setNewPhone(job?.phone || "");
      setPhoneMissing(!(job?.phone));
      setPhoneErr("");
      setPossibleMatches([]);
      setConfirming(false);
      setServerErr("");
    }
  }, [open, job]);

  // Debounced search for "existing"
  useEffect(() => {
    if (!open || mode !== "existing") return;
    const q = (searchTerm || "").trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await customersApi.search(q);
        setResults(r.filter((c) => c.customerId !== job?.customerId));
      } catch (e) { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, mode, open, job?.customerId]);

  // Debounced "possible matches" preview when creating NEW identity
  useEffect(() => {
    if (!open || mode !== "new") return;
    const q = (newName || "").trim() || (newPhone || "").trim();
    if (q.length < 2) { setPossibleMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await customersApi.search(q);
        setPossibleMatches(r.filter((c) => c.customerId !== job?.customerId));
      } catch { /* ignore */ }
    }, 350);
    return () => clearTimeout(t);
  }, [newName, newPhone, mode, open, job?.customerId]);

  const doLinkExisting = async () => {
    if (!picked) return;
    setSaving(true); setServerErr("");
    try {
      await jobsApi.linkCustomer(job.id, { customerId: picked.customerId });
      toast.success(`Ärendet kopplat till kund: ${picked.customerName}`);
      onLinked?.();
      onClose();
    } catch (e) {
      setServerErr(formatApiError(e));
    } finally { setSaving(false); }
  };

  const doCreateNew = async () => {
    const name = (newName || "").trim();
    if (!name) { setServerErr("Namn krävs."); return; }
    const phone = phoneMissing ? "" : (newPhone || "").trim();
    if (!phoneMissing && phone && !isValidSEPhone(phone)) {
      setPhoneErr("Ogiltigt svenskt mobilnummer.");
      return;
    }
    setPhoneErr("");
    setSaving(true); setServerErr("");
    try {
      await jobsApi.linkCustomer(job.id, { newCustomer: { name, phone } });
      toast.success(`Ny kundidentitet skapad och kopplad: ${name}`);
      onLinked?.();
      onClose();
    } catch (e) {
      setServerErr(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="link-customer-dialog" className="max-w-xl rounded-2xl">
        <DialogHeader>
          <DialogTitle
            className="text-xl font-bold"
            style={{ fontFamily: "Manrope, sans-serif", color: "#1A202C" }}>
            Koppla till kund
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Nuvarande kundkoppling:{" "}
            <code className="text-slate-700">{job?.customerId || "ingen"}</code>
            {" "}· Kund på ärendet:{" "}
            <strong>{job?.customerName || "—"}</strong>
            {job?.phone ? ` · ${job.phone}` : " · telefon saknas"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 border-b border-slate-100 pb-2">
          <button
            type="button"
            data-testid="link-mode-existing"
            onClick={() => setMode("existing")}
            className={`flex-1 text-sm py-2 rounded-lg inline-flex items-center justify-center gap-2 ${
              mode === "existing" ? "bg-[#E6F1FB] text-[#3B9EDA] font-semibold" : "text-slate-500 hover:bg-slate-50"
            }`}>
            <Search className="h-4 w-4" /> Sök befintlig kund
          </button>
          <button
            type="button"
            data-testid="link-mode-new"
            onClick={() => setMode("new")}
            className={`flex-1 text-sm py-2 rounded-lg inline-flex items-center justify-center gap-2 ${
              mode === "new" ? "bg-[#E6F1FB] text-[#3B9EDA] font-semibold" : "text-slate-500 hover:bg-slate-50"
            }`}>
            <UserPlus className="h-4 w-4" /> Skapa ny kundidentitet
          </button>
        </div>

        {mode === "existing" ? (
          <div className="space-y-3 py-2">
            <Label htmlFor="link-search">Sök på namn eller telefon</Label>
            <Input
              id="link-search"
              data-testid="link-search-input"
              autoComplete="off"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPicked(null); }}
              placeholder="Minst 2 tecken"
            />
            {searching && <p className="text-xs text-slate-400">Söker…</p>}
            {results.length > 0 && (
              <div data-testid="link-search-results"
                   className="border rounded-lg max-h-56 overflow-auto">
                {results.map((c) => (
                  <button
                    key={c.customerId}
                    type="button"
                    data-testid={`link-pick-${c.customerId}`}
                    onClick={() => setPicked(c)}
                    className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 ${
                      picked?.customerId === c.customerId ? "bg-[#E6F1FB]" : "hover:bg-slate-50"
                    }`}>
                    <div className="font-medium text-slate-800">{c.customerName}</div>
                    <div className="text-xs text-slate-500">
                      {c.phone || "— telefon saknas —"} · {c.jobCount} aktivt ärende{c.jobCount === 1 ? "" : "n"}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5 font-mono">{c.customerId}</div>
                  </button>
                ))}
              </div>
            )}
            {searchTerm.length >= 2 && !searching && results.length === 0 && (
              <p className="text-xs text-slate-400 italic">Inga träffar.</p>
            )}
            {picked && (
              <div data-testid="link-pick-confirmation"
                   className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
                <div className="text-xs uppercase text-slate-400 mb-1">Du är på väg att koppla ärendet till:</div>
                <div className="font-semibold text-slate-800">{picked.customerName}</div>
                <div className="text-xs text-slate-500">{picked.phone || "— telefon saknas —"}</div>
                <div className="text-[10px] text-slate-400 font-mono mt-1">{picked.customerId}</div>
                <div className="text-xs text-amber-700 mt-2 inline-flex items-start gap-1">
                  <Info className="h-3.5 w-3.5 mt-0.5 flex-none" />
                  Historiska ärenden under den gamla kopplingen rörs inte.
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-name">Kundens namn *</Label>
              <Input
                id="new-name"
                data-testid="link-new-name"
                autoComplete="off"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-phone">Telefonnummer {!phoneMissing && "(valfritt)"}</Label>
              <Input
                id="new-phone"
                data-testid="link-new-phone"
                autoComplete="off"
                value={newPhone}
                disabled={phoneMissing}
                onChange={(e) => { setNewPhone(e.target.value); if (phoneErr) setPhoneErr(""); }}
                placeholder={phoneMissing ? "— saknas —" : "0701234567"}
                className={phoneErr ? "border-red-400" : ""}
              />
              <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none mt-1">
                <input
                  type="checkbox"
                  data-testid="link-new-phone-missing"
                  checked={phoneMissing}
                  onChange={(e) => {
                    setPhoneMissing(e.target.checked);
                    if (e.target.checked) { setPhoneErr(""); setNewPhone(""); }
                  }}
                  className="h-3.5 w-3.5 accent-[#3B9EDA]"
                />
                Telefonnummer saknas
              </label>
              {phoneErr && <p className="text-xs text-red-600">{phoneErr}</p>}
            </div>
            {possibleMatches.length > 0 && (
              <div data-testid="link-possible-matches"
                   className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="text-xs text-amber-800 font-semibold mb-1 inline-flex items-center gap-1">
                  <Info className="h-3.5 w-3.5" />
                  Möjliga befintliga kunder — kontrollera att du inte skapar en dubblett
                </div>
                <ul className="text-xs text-amber-900 space-y-1">
                  {possibleMatches.slice(0, 5).map((c) => (
                    <li key={c.customerId}>
                      <strong>{c.customerName}</strong> · {c.phone || "— telefon saknas —"} · {c.jobCount} ärende{c.jobCount === 1 ? "" : "n"}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setMode("existing")}
                  className="text-xs underline text-amber-900 mt-2">
                  Växla till sökning istället
                </button>
              </div>
            )}
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
              Ny kundidentitet (nytt customerId) kommer att skapas. Namnet och telefonnumret
              här gäller det nya kundkortet och det aktuella ärendet. Historiska ärenden med
              den gamla kopplingen skrivs <strong>inte</strong> över.
            </div>
          </div>
        )}

        {serverErr && (
          <p data-testid="link-server-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {serverErr}
          </p>
        )}

        <DialogFooter className="gap-2 pt-2">
          <Button
            data-testid="link-cancel-btn"
            type="button"
            variant="outline"
            className="rounded-full px-5"
            onClick={onClose}>
            Avbryt
          </Button>
          {mode === "existing" ? (
            !confirming ? (
              <Button
                data-testid="link-review-btn"
                type="button"
                disabled={!picked}
                onClick={() => setConfirming(true)}
                className="rounded-full px-6 text-white"
                style={{ backgroundColor: "#3B9EDA" }}>
                <Link2 className="h-4 w-4 mr-2" /> Granska & koppla
              </Button>
            ) : (
              <Button
                data-testid="link-confirm-existing-btn"
                type="button"
                disabled={saving}
                onClick={doLinkExisting}
                className="rounded-full px-6 text-white"
                style={{ backgroundColor: "#3B9EDA" }}>
                {saving ? "Kopplar…" : "Bekräfta koppling"}
              </Button>
            )
          ) : (
            !confirming ? (
              <Button
                data-testid="link-review-new-btn"
                type="button"
                disabled={!newName.trim()}
                onClick={() => setConfirming(true)}
                className="rounded-full px-6 text-white"
                style={{ backgroundColor: "#3B9EDA" }}>
                <UserPlus className="h-4 w-4 mr-2" /> Granska & skapa
              </Button>
            ) : (
              <Button
                data-testid="link-confirm-new-btn"
                type="button"
                disabled={saving}
                onClick={doCreateNew}
                className="rounded-full px-6 text-white"
                style={{ backgroundColor: "#3B9EDA" }}>
                {saving ? "Skapar…" : "Bekräfta ny kund"}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkCustomerDialog;
